import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Hero } from "@/components/Hero";
import { TrustStrip } from "@/components/TrustStrip";
import { SectionHeading } from "@/components/SectionHeading";
import { ServiceCard } from "@/components/ServiceCard";
import { DoctorCard } from "@/components/DoctorCard";
import { FacilityCard } from "@/components/FacilityCard";
import { TestimonialCard } from "@/components/TestimonialCard";
import { FAQ } from "@/components/FAQ";
import { AppointmentCTA } from "@/components/AppointmentCTA";
import { Icon } from "@/utils/icons";
import { SITE } from "@/config/site";
import { SERVICES } from "@/data/services";
import { DOCTORS } from "@/data/doctors";
import { FACILITIES } from "@/data/facilities";
import { TESTIMONIALS } from "@/data/testimonials";
import { FAQS } from "@/data/faqs";
import { WHY_CHOOSE_US } from "@/data/whyChooseUs";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  name: SITE.name,
  slogan: SITE.tagline,
  telephone: SITE.phoneRaw,
  address: {
    "@type": "PostalAddress",
    addressLocality: SITE.city,
    addressRegion: SITE.state,
    addressCountry: "IN",
  },
};

const ABOUT_POINTS = [
  "Qualified, approachable doctors",
  "Clean and modern clinical environment",
  "Easy WhatsApp appointment requests",
  "Care for the whole family, close to home",
];

export default function Home() {
  return (
    <>
      <Seo
        title={`${SITE.name} — Hospital in Kasdol, Chhattisgarh`}
        siteName={SITE.name}
        description={`${SITE.name} in Kasdol, Chhattisgarh — ${SITE.tagline} Book appointments on WhatsApp, meet our doctors and explore our services.`}
        image="/images/hospital-hero.jpg"
        jsonLd={jsonLd}
      />
      <Hero />
      <TrustStrip />

      {/* About teaser */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="home-about-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div className="relative order-2 lg:order-1">
            <img
              src="/images/medical-team.jpg"
              alt="Doctor discussing care with a patient"
              loading="lazy"
              className="w-full rounded-3xl object-cover shadow-xl"
            />
            <div className="absolute -bottom-6 -right-2 hidden rounded-2xl bg-navy-900 px-6 py-4 text-white shadow-xl sm:block">
              <p className="font-heading text-lg font-bold">{SITE.shortName}</p>
              <p className="text-xs text-slate-300">{SITE.tagline}</p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionHeading
              align="left"
              eyebrow="About the Hospital"
              title="Quality healthcare, closer to home in Kasdol"
              description={`${SITE.name} serves Kasdol and nearby communities with dependable medical care, clear communication and a calm, patient-first environment.`}
            />
            <ul className="-mt-4 space-y-3">
              {ABOUT_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-navy-800 transition-colors hover:text-emerald-600"
              data-testid="home-about-link"
            >
              More about us <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Our Services"
            title="Care for everyday and ongoing health needs"
            description="The services below are editable demo content and will be updated with the hospital's confirmed service list."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {SERVICES.slice(0, 6).map((s) => <ServiceCard key={s.slug} service={s} />)}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border-2 border-navy-200 px-6 py-3 text-sm font-semibold text-navy-800 transition-colors hover:border-navy-800 hover:bg-navy-50"
              data-testid="home-services-view-all"
            >
              View all services <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="bg-navy-900 py-16 sm:py-20 lg:py-24" data-testid="home-why-us-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            light
            eyebrow="Why Choose Us"
            title={`Why families in Kasdol choose ${SITE.shortName}`}
            description="We keep healthcare simple: listen well, explain clearly and treat every patient with dignity."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE_US.map((w) => (
              <div key={w.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 transition-colors duration-300 hover:bg-white/10" data-testid={`why-us-${w.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-400">
                  <Icon name={w.icon} className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-white">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{w.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="home-doctors-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Our Doctors"
            title="Meet our medical team"
            description="Doctor profiles shown are sample placeholders — real doctor details will replace them shortly."
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {DOCTORS.map((d) => <DoctorCard key={d.slug} doctor={d} />)}
          </div>
        </div>
      </section>

      {/* Facilities preview */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="home-facilities-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Facilities"
            title="A calm, clean environment for your care"
            description="Facility information shown is demo content and will be updated with the hospital's actual facilities."
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {FACILITIES.slice(0, 3).map((f) => <FacilityCard key={f.slug} facility={f} />)}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/facilities"
              className="inline-flex items-center gap-2 rounded-full border-2 border-navy-200 px-6 py-3 text-sm font-semibold text-navy-800 transition-colors hover:border-navy-800 hover:bg-navy-50"
              data-testid="home-facilities-view-all"
            >
              Explore all facilities <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      <AppointmentCTA />

      {/* Testimonials */}
      <section className="py-16 sm:py-20 lg:py-24" data-testid="home-testimonials-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Testimonials"
            title="What our patients say"
            description="Genuine patient feedback will be published here soon — the cards below are demo placeholders, not real reviews."
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} testimonial={t} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="home-faq-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently asked questions"
            description="Quick answers about appointments, timings and reaching the hospital."
          />
          <FAQ items={FAQS.slice(0, 6)} />
        </div>
      </section>
    </>
  );
}
